// Utilidades geodésicas y de geometría de líneas. Todo trabaja en grados WGS84 con la
// convención LatLng { lat, lon } — coincide con los GeoJSON del dataset (Point [lon, lat]
// se convierte al cargar en data.ts).

export interface LatLng {
  lat: number;
  lon: number;
}

const R = 6_371_008.8; // radio terrestre medio (m) — WGS84 mean radius

const toRad = (deg: number) => (deg * Math.PI) / 180;

// Distancia esférica entre dos puntos. Suficiente para las distancias urbanas de Elche
// (error <0.5 % frente a Vincenty en distancias <20 km).
export function haversine(a: LatLng, b: LatLng): number {
  const dLat = toRad(b.lat - a.lat);
  const dLon = toRad(b.lon - a.lon);
  const la1 = toRad(a.lat);
  const la2 = toRad(b.lat);
  const s =
    Math.sin(dLat / 2) ** 2 + Math.cos(la1) * Math.cos(la2) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
}

// Proyección plana local (equirectangular alrededor de un punto de referencia). Se usa para
// resolver problemas de segmento-punto en metros con álgebra de vectores en el plano.
// El error para distancias del orden del kilómetro cerca del ecuador de Elche es despreciable.
function toXY(p: LatLng, ref: LatLng): { x: number; y: number } {
  const cosRefLat = Math.cos(toRad(ref.lat));
  return {
    x: toRad(p.lon - ref.lon) * R * cosRefLat,
    y: toRad(p.lat - ref.lat) * R,
  };
}
function fromXY(xy: { x: number; y: number }, ref: LatLng): LatLng {
  const cosRefLat = Math.cos(toRad(ref.lat));
  return {
    lon: ref.lon + (xy.x / (R * cosRefLat)) * (180 / Math.PI),
    lat: ref.lat + (xy.y / R) * (180 / Math.PI),
  };
}

// Proyecta un punto sobre un segmento. Devuelve el punto proyectado, la distancia perpendicular
// (metros) y el parámetro t ∈ [0, 1] a lo largo del segmento.
export interface Projection {
  point: LatLng;
  distance: number;
  t: number;
}
export function projectOnSegment(p: LatLng, a: LatLng, b: LatLng): Projection {
  const A = toXY(a, a);
  const B = toXY(b, a);
  const P = toXY(p, a);
  const abx = B.x - A.x;
  const aby = B.y - A.y;
  const lenSq = abx * abx + aby * aby;
  let t = 0;
  if (lenSq > 0) t = ((P.x - A.x) * abx + (P.y - A.y) * aby) / lenSq;
  t = Math.max(0, Math.min(1, t));
  const projXY = { x: A.x + t * abx, y: A.y + t * aby };
  const dx = P.x - projXY.x;
  const dy = P.y - projXY.y;
  return {
    point: fromXY(projXY, a),
    distance: Math.sqrt(dx * dx + dy * dy),
    t,
  };
}

// Longitud (metros) de una polilínea abierta.
export function polylineLength(line: LatLng[]): number {
  let total = 0;
  for (let i = 1; i < line.length; i++) total += haversine(line[i - 1]!, line[i]!);
  return total;
}

// Localiza el segmento más cercano del `MultiLineString` a un punto. Devuelve el índice del
// tramo, el índice del segmento dentro del tramo, la distancia (m) y el t del segmento.
export interface NearestOnMultiLine {
  pathIndex: number;
  segIndex: number;
  distance: number;
  t: number;
  point: LatLng;
}
export function nearestPointOnMultiLine(
  point: LatLng,
  multiLine: LatLng[][],
): NearestOnMultiLine | null {
  let best: NearestOnMultiLine | null = null;
  for (let p = 0; p < multiLine.length; p++) {
    const path = multiLine[p]!;
    for (let s = 0; s < path.length - 1; s++) {
      const pr = projectOnSegment(point, path[s]!, path[s + 1]!);
      if (best === null || pr.distance < best.distance) {
        best = {
          pathIndex: p,
          segIndex: s,
          distance: pr.distance,
          t: pr.t,
          point: pr.point,
        };
      }
    }
  }
  return best;
}

// Devuelve la sub-polilínea entre A y B, siempre y cuando ambos caigan en el mismo tramo (path).
// Si caen en tramos distintos o no hay geometría útil, devuelve null y el llamante debe caer al
// segmento recto entre las dos paradas — la spec lo permite explícitamente porque el dataset
// tiene huecos en los trazados.
export function clipBetween(
  multiLine: LatLng[][],
  a: LatLng,
  b: LatLng,
): LatLng[] | null {
  const na = nearestPointOnMultiLine(a, multiLine);
  const nb = nearestPointOnMultiLine(b, multiLine);
  if (!na || !nb) return null;
  if (na.pathIndex !== nb.pathIndex) return null;
  const path = multiLine[na.pathIndex]!;
  // Determinar orden A→B a lo largo del path.
  const first =
    na.segIndex < nb.segIndex || (na.segIndex === nb.segIndex && na.t <= nb.t) ? na : nb;
  const last = first === na ? nb : na;
  const out: LatLng[] = [first.point];
  for (let s = first.segIndex + 1; s <= last.segIndex; s++) {
    out.push(path[s]!);
  }
  out.push(last.point);
  // Si el que va primero era B, invertimos para devolver siempre en dirección A→B.
  if (first === nb) out.reverse();
  return out;
}
